import { useEffect, useRef } from 'react';

// Build SSE URL with auth token as query param (EventSource can't set headers)
const buildSseUrl = (path) => {
  let url = process.env.REACT_APP_IP + path;
  try {
    const { getToken } = require('./auth');
    const token = getToken();
    if (token) url += (url.includes('?') ? '&' : '?') + 'token=' + encodeURIComponent(token);
  } catch (e) { /* auth not available */ }
  return url;
};

/**
 * SSE hook with automatic polling fallback.
 *
 * - Tries EventSource first (server-push, zero traffic when data is unchanged)
 * - Falls back to polling if SSE endpoint is unavailable or errors
 * - Pauses polling when tab is hidden, resumes immediately on focus
 * - Properly cleans up on unmount
 *
 * @param {string|null} ssePath    e.g. '/SSE/LiveAgents' or null to skip SSE entirely
 * @param {string}      eventName  SSE event type, e.g. 'liveagents'
 * @param {function}    onData     called with parsed JSON on each SSE event
 * @param {function}    fallbackFn async (signal) => void  — used for polling
 * @param {number}      fallbackMs polling interval in ms (only used if SSE fails)
 * @param {Array}       deps       useEffect dependency array
 * @param {object}      options    { enabled: bool }
 */
export default function useSSE(ssePath, eventName, onData, fallbackFn, fallbackMs, deps = [], { enabled = true } = {}) {
  const esRef = useRef(null);
  const timerRef = useRef(null);
  const abortRef = useRef(null);
  // null=unknown, true=SSE working, false=SSE failed→polling
  const sseOkRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    if (!enabled) return;

    mountedRef.current = true;
    sseOkRef.current = null;

    const stopPolling = () => {
      clearTimeout(timerRef.current);
      timerRef.current = null;
      abortRef.current?.abort();
    };

    const poll = async () => {
      if (document.hidden || !mountedRef.current) return;
      abortRef.current?.abort();
      abortRef.current = new AbortController();
      try {
        await fallbackFn(abortRef.current.signal);
      } catch (e) {
        if (e.name !== 'AbortError' && e.name !== 'CanceledError') console.error('useSSE poll error:', e);
      }
      if (mountedRef.current) timerRef.current = setTimeout(poll, fallbackMs);
    };

    const startPolling = () => {
      sseOkRef.current = false;
      if (esRef.current) { esRef.current.close(); esRef.current = null; }
      poll();
    };

    const connectSSE = () => {
      if (esRef.current) { esRef.current.close(); esRef.current = null; }
      if (!ssePath || !localStorage.getItem('roles')) { startPolling(); return; }

      let es;
      try { es = new EventSource(buildSseUrl(ssePath)); }
      catch (e) { startPolling(); return; }
      esRef.current = es;

      es.addEventListener(eventName, (e) => {
        if (!mountedRef.current) return;
        sseOkRef.current = true;
        stopPolling();
        try { onData(JSON.parse(e.data)); } catch (err) { console.error('useSSE parse error:', err); }
      });

      es.onerror = () => {
        es.close();
        if (esRef.current === es) esRef.current = null;
        if (!mountedRef.current) return;
        if (sseOkRef.current === true) {
          // Was working — reconnect after delay
          sseOkRef.current = null;
          setTimeout(connectSSE, 5000);
        } else {
          // Never connected — SSE endpoint not available, use polling
          startPolling();
        }
      };
    };

    connectSSE();

    // Immediate fetch so we don't wait for SSE handshake on first load
    const initAc = new AbortController();
    fallbackFn(initAc.signal).catch(() => {});

    const onVisibility = () => {
      if (document.hidden || !mountedRef.current) return;
      if (sseOkRef.current === false) {
        clearTimeout(timerRef.current);
        poll();
      } else if (!esRef.current) {
        connectSSE();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      mountedRef.current = false;
      stopPolling();
      initAc.abort();
      if (esRef.current) { esRef.current.close(); esRef.current = null; }
      document.removeEventListener('visibilitychange', onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, enabled]);
}
